import type { FileError } from 'react-dropzone';

import type { DcmTagSheetTag } from '../../../../../../apis/campaignGraphqlManager/dcmTagSheet/types';
import type { DisplayEnum, EventType } from '../../../../../../configs';
import {
  adTagEvents,
  adTagTypes,
  convertAdTagEvent,
  convertAdTagType,
  convertAdType,
  convertCreative,
  getPermittedAdTagEvents,
  isRecognizedConfig,
} from '../../../../../../configs';
import type { LineItemModel } from '../../../../TraffickingPage/modelConverters';
import { getDefaultPositionForType } from '../../components/TagRow/utils';
import type {
  RawTagSheetAd,
  RawTagSheetParseResultWithAdIds,
  TagSheetAd,
  TagSheetLineItem,
  TagSheetParseResult,
  TagSheetTag,
} from '../../types';
import { MAX_TAG_SHEET_FILE_SIZE, TAG_SHEET_FILE_SIZE_ERROR } from './constants';

export const tagSheetFileValidator = (file: File): FileError | null => {
  if (file.size > MAX_TAG_SHEET_FILE_SIZE) {
    return TAG_SHEET_FILE_SIZE_ERROR;
  }

  return null;
};

// TODO DANADS-2170: confirm that setting default position (COMPLETE) for unrecognized types is acceptable
const getPositionForTag = (rawTag: DcmTagSheetTag, lineItemBillableEvent?: string | null): DisplayEnum<EventType> => {
  const type = convertAdTagType(rawTag.type);

  if (!isRecognizedConfig(type)) {
    return rawTag.position ? convertAdTagEvent(rawTag.position) : adTagEvents.COMPLETE;
  }

  if (!rawTag.position) {
    return convertAdTagEvent(getDefaultPositionForType(type.key, undefined, lineItemBillableEvent));
  }

  if (type.key === adTagTypes.BILLABLE.key) {
    const permitted = getPermittedAdTagEvents(type.key, lineItemBillableEvent ?? undefined);
    const convertedPosition = convertAdTagEvent(rawTag.position);

    if (isRecognizedConfig(convertedPosition)) {
      const isPermitted = permitted.some((p) => p.key === convertedPosition.key);

      if (isPermitted) return convertedPosition;
    }

    return permitted[0];
  }

  return convertAdTagEvent(rawTag.position);
};

const convertRawTag = (rawTag: DcmTagSheetTag, lineItemBillableEvent?: string | null): TagSheetTag => ({
  type: convertAdTagType(rawTag.type),
  tagUrl: rawTag.tagUrl,
  position: getPositionForTag(rawTag, lineItemBillableEvent),
});

const convertRawAd = (rawAd: RawTagSheetAd, lineItemBillableEvent?: string | null): TagSheetAd => ({
  ...rawAd,
  adType: convertAdType(rawAd.adType),
  creative: {
    ...rawAd.creative,
    creativeType: convertCreative(rawAd.creative.creativeType),
  },
  tags: rawAd.tags.map((tag) => convertRawTag(tag, lineItemBillableEvent)),
});

type ParseResultMergedWithLineItems = {
  result: TagSheetParseResult;
  unmatchedLineItemIds: string[];
};

export const handleParsedResultAndMergeWithLineItems = (
  enrichedResult: RawTagSheetParseResultWithAdIds,
  lineItemModels: LineItemModel[]
): ParseResultMergedWithLineItems => {
  const lineItemModelMap = new Map(lineItemModels.map((li) => [li.id, li]));

  const parsedAdsMap = new Map(
    enrichedResult.lineItems.map((parsedLi) => {
      const model = lineItemModelMap.get(parsedLi.lineItemId);
      const billableEvent = model?.billableEvent;

      return [parsedLi.lineItemId, parsedLi.ads.map((ad: RawTagSheetAd) => convertRawAd(ad, billableEvent))];
    })
  );

  const mergedLineItems: TagSheetLineItem[] = lineItemModels.map((model) => ({
    ...model,
    tagSheetAds: parsedAdsMap.get(model.id) || [],
  }));

  const unmatchedLineItemIds: string[] = [];

  enrichedResult.lineItems.forEach((parsedLi) => {
    if (!lineItemModelMap.has(parsedLi.lineItemId)) {
      unmatchedLineItemIds.push(parsedLi.lineItemId);
    }
  });

  return {
    result: {
      unmappedAds: enrichedResult.unmappedAds.map((ad) => convertRawAd(ad)),
      lineItems: mergedLineItems,
      vendor: enrichedResult.vendor,
    },
    unmatchedLineItemIds,
  };
};
